import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraCheckbox, SpecraFormField, SpecraInput, SpecraOverlayDialog, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useConvertToContract } from '../../../hooks/useProjects';
import { apiErrorMessage } from '../../../lib/api/apiErrorMessage';
import { useTenantId } from '../../../pages/Delivery/_shared/hooks/useTenantId';
import { businessUnitHooks } from '../../../pages/Delivery/Org/hooks';
const CONTRACT_TYPES = [
    {
        value: 'FFP',
        label: 'Firm Fixed Price (FFP)'
    },
    {
        value: 'T&M',
        label: 'Time & Materials (T&M)'
    },
    {
        value: 'CPFF',
        label: 'Cost Plus Fixed Fee (CPFF)'
    },
    {
        value: 'CPIF',
        label: 'Cost Plus Incentive Fee (CPIF)'
    },
    {
        value: 'CPAF',
        label: 'Cost Plus Award Fee (CPAF)'
    },
    {
        value: 'IDIQ',
        label: 'Indefinite Delivery/Indefinite Quantity (IDIQ)'
    },
    {
        value: 'BPA',
        label: 'Blanket Purchase Agreement (BPA)'
    },
    {
        value: 'HYBRID',
        label: 'Hybrid'
    },
    {
        value: 'OTHER',
        label: 'Other'
    }
];
/**
 * Convert a proposal to a contract project (FND-26: ported as-is from the
 * retired legacy dashboard, rebuilt on Specra components). Opened from the v2
 * project-actions menu; owners only.
 */ export function ConvertToContractDialog({ open, onOpenChange, project }) {
    const navigate = useNavigate();
    const convertMutation = useConvertToContract();
    const tenantId = useTenantId();
    const businessUnits = businessUnitHooks.useList({
        companyId: tenantId
    });
    const [name, setName] = useState(`[Contract] ${project.name}`);
    const [contractNumber, setContractNumber] = useState('');
    const [contractType, setContractType] = useState('');
    const [archiveSource, setArchiveSource] = useState(false);
    const [businessUnitId, setBusinessUnitId] = useState('');
    const [touched, setTouched] = useState(false);
    const trimmedName = name.trim();
    const isNameEmpty = trimmedName.length === 0;
    const showNameError = touched && isNameEmpty;
    const pending = convertMutation.isPending;
    useEffect(()=>{
        if (open) {
            setName(`[Contract] ${project.name}`);
            setContractNumber('');
            setContractType('');
            setArchiveSource(false);
            setBusinessUnitId('');
            setTouched(false);
        }
    }, [
        open,
        project
    ]);
    const handleSubmit = async (e)=>{
        e.preventDefault();
        setTouched(true);
        if (isNameEmpty) return;
        try {
            const result = await convertMutation.mutateAsync({
                projectId: project.projectId,
                name: trimmedName,
                contractNumber: contractNumber.trim() || undefined,
                contractType: contractType || undefined,
                archiveSourceProposal: archiveSource,
                businessUnitId: businessUnitId || undefined
            });
            const summary = result.conversionSummary;
            toast.success(`Contract project created. Carried forward: ${summary.requirementsCopied} requirements, ${summary.clinsCopied} CLINs, ${summary.deliverablesCopied} deliverables.`);
            for (const warning of summary.warnings ?? []){
                toast.warning(warning);
            }
            onOpenChange(false);
            navigate(`/contract/${result.project.projectId}`);
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Failed to convert proposal to contract'));
        }
    };
    const formId = 'convert-to-contract-form';
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        description: "Create a new contract project from this proposal. Requirements, CLINs, deliverables, and source documents will be carried forward.",
        footerProps: {
            cancelProps: {
                'data-testid': Testids.convertToContract.cancelButton,
                disabled: pending,
                type: 'button'
            },
            confirmDisabled: showNameError,
            confirmLabel: 'Convert to Contract',
            confirmProps: {
                'data-testid': Testids.convertToContract.submitButton,
                form: formId,
                type: 'submit'
            },
            loading: pending,
            loadingLabel: 'Converting…',
            onCancel: ()=>onOpenChange(false)
        },
        onOpenChange: onOpenChange,
        open: open,
        size: "md",
        testId: Testids.convertToContract.dialog,
        title: "Convert to Contract",
        children: /*#__PURE__*/ _jsxs("form", {
            className: "space-y-[var(--space-4)]",
            id: formId,
            onSubmit: handleSubmit,
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    error: showNameError ? 'Contract name is required' : undefined,
                    label: "Contract Project Name",
                    required: true,
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        "data-testid": Testids.convertToContract.nameInput,
                        disabled: pending,
                        id: "contract-name",
                        invalid: showNameError,
                        maxLength: 200,
                        onBlur: ()=>setTouched(true),
                        onChange: (e)=>setName(e.target.value),
                        placeholder: "e.g., [Contract] NASA Cloud Services",
                        required: true,
                        value: name
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    hint: "Optional",
                    label: "Contract Number",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        "data-testid": Testids.convertToContract.numberInput,
                        disabled: pending,
                        id: "contract-number",
                        maxLength: 100,
                        onChange: (e)=>setContractNumber(e.target.value),
                        placeholder: "e.g., GS-35F-0001X",
                        value: contractNumber
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    hint: "Optional",
                    label: "Contract Type",
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        "data-testid": Testids.convertToContract.typeSelect,
                        disabled: pending,
                        id: "contract-type",
                        onChange: (e)=>setContractType(e.target.value),
                        value: contractType,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Select contract type..."
                            }),
                            CONTRACT_TYPES.map((ct)=>/*#__PURE__*/ _jsx("option", {
                                    value: ct.value,
                                    children: ct.label
                                }, ct.value))
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    hint: "Scopes the new contract's delivery program to this business unit. Requires the delivery-VP bundle for that unit (or company-wide) to create it.",
                    label: "Business Unit",
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        "data-testid": Testids.deliveryPrograms.businessUnitSelect,
                        disabled: pending,
                        id: "contract-business-unit",
                        onChange: (e)=>setBusinessUnitId(e.target.value),
                        value: businessUnitId,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Company-wide (no business unit)"
                            }),
                            (businessUnits.data?.items ?? []).map((bu)=>/*#__PURE__*/ _jsx("option", {
                                    value: bu.id,
                                    children: bu.name
                                }, bu.id))
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    htmlFor: "archive-source",
                    label: "Archive source proposal after conversion",
                    children: /*#__PURE__*/ _jsx(SpecraCheckbox, {
                        checked: archiveSource,
                        "data-testid": Testids.convertToContract.archiveCheckbox,
                        disabled: pending,
                        id: "archive-source",
                        onCheckedChange: (v)=>setArchiveSource(v === true)
                    })
                })
            ]
        })
    });
}
